/**
 * ReportsContext
 *
 * Provides caching for the reports list so that navigating away from it
 * (e.g. to a report detail page) and returning does not trigger a full reload.
 * The context stores the reports, pagination state, filters, and view settings.
 *
 * Mirrors JournalArticlesContext without the article-only state (collections,
 * PR Bulk Review, topics, author contacts). Sorting is always enabled.
 */

import { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from "react"
import { JournalArticle, extractionStatusLabelMap } from "@/services/journalArticlesService"
import {
  REPORT_TYPE_UNCLASSIFIED,
  ReportsFilters,
  ReportTypeFilterValue,
  getReports,
} from "@/services/reportsService"
import { useAuth } from "@/hooks/useAuth"
import {
  ReportsSidebarOption,
  DEFAULT_REPORTS_SIDEBAR_OPTION,
  reportTypeFromOption,
} from "@/components/reports/reportsRoutes"
import { GenericSortConfig } from "@/components/survey-catalogue/SortableColumnHeader"
import { ViewMode } from "@/components/shared/TableViewSelector"
import { getColumnGroupsForViewMode } from "@/components/reports/columns"

/** Sortable column key type for reports (the API only sorts by year). */
export type ReportSortableKey = "publicationYear"

/** Default page size for report pagination. */
const DEFAULT_PAGE_SIZE: number = 50

/** Default view mode for the reports table. */
const DEFAULT_VIEW_MODE: ViewMode = "compact"

/** Number of days the Recently Added view looks back. */
const RECENTLY_ADDED_DAYS: number = 7

/** Milliseconds in one day, used to compute the Recently Added cutoff. */
const MS_PER_DAY: number = 24 * 60 * 60 * 1000

/**
 * Reverse of extractionStatusLabelMap: display label → API value
 * (e.g. "Not Started" → "NOT_STARTED"). Filters hold labels; the API wants values.
 * A Map so a label such as "constructor" cannot hit an Object prototype member.
 */
const EXTRACTION_STATUS_LABEL_TO_VALUE: ReadonlyMap<string, string> = new Map(
  Object.entries(extractionStatusLabelMap).map(
    ([value, label]: [string, string]): [string, string] => [label, value]
  )
)

/**
 * The activeFilters keys the reports table offers. Each is also the name of
 * the ReportsFilters field (and API query parameter) it maps to.
 */
const STATUS_FILTER_KEYS = ["prExtractionStatus", "rsExtractionStatus"] as const

/** One of the STATUS_FILTER_KEYS. */
type StatusFilterKey = (typeof STATUS_FILTER_KEYS)[number]

/**
 * Check whether an activeFilters key is one the reports table supports.
 *
 * @param key - The activeFilters map key.
 * @returns True (and narrows the type) when `key` is a StatusFilterKey.
 */
function isStatusFilterKey(key: string): key is StatusFilterKey {
  // Widen the readonly literal tuple so `includes` accepts any string.
  return (STATUS_FILTER_KEYS as readonly string[]).includes(key)
}

/**
 * Format a Date as YYYY-MM-DD in UTC.
 *
 * UTC (via toISOString) matches how JournalArticlesContext computes its
 * Recently Added date, so both sections use the same cutoff day.
 *
 * @param date - The date to format.
 * @returns The UTC calendar date, e.g. "2026-10-20".
 */
function toUtcDateString(date: Date): string {
  return date.toISOString().split("T")[0]
}

/** Input to buildReportsFilters: the context state that shapes a request. */
export interface BuildReportsFiltersInput {
  /** The selected sidebar option. */
  sidebarOption: ReportsSidebarOption
  /** The search box text. Sent only when non-empty. */
  searchQuery: string
  /**
   * Active table filters: key → set of selected display labels. Keys must be
   * "prExtractionStatus" or "rsExtractionStatus".
   */
  activeFilters: Map<string, Set<string>>
  /** The current sort, or null for the API default (newest first). */
  sortConfig: GenericSortConfig<ReportSortableKey> | null
  /** The current time, injected so the Recently Added cutoff is testable. */
  now: Date
}

/**
 * Translate the reports context state into API filters.
 *
 * - A report-type sidebar option sets `reportType`; Needs Review sets it to
 *   "unclassified" (reports with no reportType).
 * - Recently Added sets `createdAfter` to `now` minus 7 days (UTC, YYYY-MM-DD).
 * - PR / RS extraction status labels become comma-joined API values.
 * - `searchTerm` is set only when the search query is non-empty. Like the
 *   article context, the query is not trimmed here.
 * - `sortBy` / `sortDirection` come from `sortConfig`.
 *
 * Only keys with a value are present in the result, so the returned object
 * contains exactly the filters that will be sent.
 *
 * @param input - The sidebar option, search query, active filters, sort, and current time.
 * @returns The ReportsFilters for getReports.
 * @throws Error when activeFilters has a key other than the two status
 *   filters, or a status label missing from extractionStatusLabelMap (for
 *   example from a stale URL). Failing loudly avoids silently dropping a
 *   filter the user believes is applied.
 */
export function buildReportsFilters({
  sidebarOption,
  searchQuery,
  activeFilters,
  sortConfig,
  now,
}: BuildReportsFiltersInput): ReportsFilters {
  const filters: ReportsFilters = {}

  // Sidebar option → reportType (type options and Needs Review) or
  // createdAfter (Recently Added).
  const reportType: ReportTypeFilterValue | null =
    sidebarOption === "needs-review" ? REPORT_TYPE_UNCLASSIFIED : reportTypeFromOption(sidebarOption)
  if (reportType !== null) {
    filters.reportType = reportType
  }
  if (sidebarOption === "recently-added") {
    filters.createdAfter = toUtcDateString(new Date(now.getTime() - RECENTLY_ADDED_DAYS * MS_PER_DAY))
  }

  if (searchQuery) {
    filters.searchTerm = searchQuery
  }

  // Status filters: labels → API values, comma-joined. Empty sets send nothing.
  activeFilters.forEach((labels: Set<string>, key: string): void => {
    if (!isStatusFilterKey(key)) {
      throw new Error(`Unknown reports filter "${key}"`)
    }
    if (labels.size === 0) {
      return
    }
    const apiValues: string[] = [...labels].map((label: string): string => {
      const value: string | undefined = EXTRACTION_STATUS_LABEL_TO_VALUE.get(label)
      if (value === undefined) {
        throw new Error(`Unknown extraction status "${label}" in filter "${key}"`)
      }
      return value
    })
    filters[key] = apiValues.join(",")
  })

  if (sortConfig !== null) {
    filters.sortBy = sortConfig.key
    filters.sortDirection = sortConfig.direction
  }

  return filters
}

/**
 * Context value shape with state and actions.
 */
export interface ReportsContextValue {
  /** The list of reports currently loaded. */
  reports: JournalArticle[]
  /** Total number of reports matching the current filters. */
  totalCount: number
  /** Total number of pages available. */
  totalPages: number
  /** Current page index (0-based). */
  pageIndex: number
  /** Number of items per page. */
  pageSize: number
  /** Current sidebar selection (determines which reports to show). */
  sidebarOption: ReportsSidebarOption
  /** Current search query string. */
  searchQuery: string
  /** Active table filters (key → set of selected labels). */
  activeFilters: Map<string, Set<string>>
  /** Current sort configuration, or null for the API default. */
  sortConfig: GenericSortConfig<ReportSortableKey> | null
  /** Current view mode (compact, status, full). */
  viewMode: ViewMode
  /** Set of visible column keys. */
  visibleColumns: Set<string>
  /** Whether data is currently being fetched. */
  loading: boolean
  /** Error message if the last fetch failed. */
  error: string | null
  /** Timestamp of the last successful fetch (null until the first one). */
  lastFetchTime: number | null
  /** Currently selected report (for the details panel). */
  selectedReport: JournalArticle | null
  /** ID of the currently selected report. */
  selectedReportId: number | null
  /** Fetch a page of reports with the current filters. */
  fetchPage: (page: number) => Promise<void>
  /** Set the current page index. */
  setPageIndex: (index: number) => void
  /** Set the page size (resets to the first page and closes the panel). */
  setPageSize: (size: number) => void
  /** Set the sidebar option (resets page, search, and selection). */
  setSidebarOption: (option: ReportsSidebarOption) => void
  /** Set the search query (resets page and selection). */
  setSearchQuery: (query: string) => void
  /** Set the active filters (resets page and selection). */
  setActiveFilters: (filters: Map<string, Set<string>>) => void
  /** Set the sort configuration (resets page). */
  setSortConfig: (config: GenericSortConfig<ReportSortableKey> | null) => void
  /** Toggle sort for a given column key: asc → desc → none. */
  toggleSort: (key: ReportSortableKey) => void
  /** Set the view mode. */
  setViewMode: (mode: ViewMode) => void
  /** Set the visible columns. */
  setVisibleColumns: (columns: Set<string>) => void
  /** Select a report (opens the details panel). */
  selectReport: (report: JournalArticle) => void
  /** Clear the selected report (closes the details panel). */
  clearSelectedReport: () => void
  /** Replace a report in the cached list (and the selection, if it matches). */
  updateReportInList: (updated: JournalArticle) => void
}

const ReportsContext = createContext<ReportsContextValue | null>(null)

/** Props for the ReportsProvider component. */
interface ReportsProviderProps {
  /** Child components that can access the context. */
  children: ReactNode
}

/**
 * Provider component that manages reports state and caching.
 *
 * Mounted once in the app layout route so the cached list survives navigating
 * to a report detail page and back. Page state is seeded from the URL by
 * useReportsPage, not by provider props.
 *
 * @param props - Provider props.
 * @param props.children - Components that can access the context.
 * @returns The provider component wrapping children.
 */
export function ReportsProvider({ children }: ReportsProviderProps): React.JSX.Element {
  const { getAccessToken } = useAuth()

  // Core data state
  const [reports, setReports] = useState<JournalArticle[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [totalPages, setTotalPages] = useState<number>(1)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [lastFetchTime, setLastFetchTime] = useState<number | null>(null)

  // Pagination state
  const [pageIndex, setPageIndex] = useState<number>(0)
  const [pageSize, setPageSizeState] = useState<number>(DEFAULT_PAGE_SIZE)

  // Filter and view state
  const [sidebarOption, setSidebarOptionState] = useState<ReportsSidebarOption>(DEFAULT_REPORTS_SIDEBAR_OPTION)
  const [searchQuery, setSearchQueryState] = useState<string>("")
  const [activeFilters, setActiveFiltersState] = useState<Map<string, Set<string>>>(new Map())
  const [sortConfig, setSortConfigState] = useState<GenericSortConfig<ReportSortableKey> | null>(null)
  const [viewMode, setViewModeState] = useState<ViewMode>(DEFAULT_VIEW_MODE)
  // Every column of the default view starts visible. The initializer function
  // runs once, on the first render only.
  const [visibleColumns, setVisibleColumnsState] = useState<Set<string>>(
    (): Set<string> => new Set(getColumnGroupsForViewMode(DEFAULT_VIEW_MODE).flatMap((g) => g.keys))
  )

  // Selection state
  const [selectedReport, setSelectedReport] = useState<JournalArticle | null>(null)
  const [selectedReportId, setSelectedReportId] = useState<number | null>(null)

  // Mirror of selectedReportId kept in a ref so fetchPage can read the latest
  // value WITHOUT listing it as a dependency. If fetchPage depended on
  // selectedReportId, selecting a report would recreate fetchPage, and the
  // page's fetch effect (which depends on fetchPage) would reload the list.
  const selectedReportIdRef = useRef<number | null>(null)
  useEffect(() => {
    selectedReportIdRef.current = selectedReportId
  }, [selectedReportId])

  // Incremented on every fetch; a response whose id no longer matches belongs
  // to a superseded request and is discarded.
  const fetchIdRef = useRef<number>(0)

  /**
   * Fetch a page of reports from the API.
   *
   * Filter building runs inside the try block, so a throw from
   * buildReportsFilters (e.g. an unknown status label) becomes the context
   * `error` rather than an unhandled rejection.
   *
   * @param page - Zero-based page index to fetch.
   * @returns A promise that resolves once state has been updated.
   */
  const fetchPage = useCallback(
    async (page: number): Promise<void> => {
      const currentFetchId: number = ++fetchIdRef.current

      setLoading(true)
      setError(null)

      try {
        const filters: ReportsFilters = buildReportsFilters({
          sidebarOption,
          searchQuery,
          activeFilters,
          sortConfig,
          now: new Date(),
        })
        const token: string = await getAccessToken()
        // The API is 1-based; the context's page index is 0-based.
        const data = await getReports(token, page + 1, pageSize, filters)

        // Discard stale responses
        if (currentFetchId !== fetchIdRef.current) {
          return
        }

        setReports(data.items)
        setTotalCount(data.totalCount)
        setTotalPages(data.totalPages)
        setLastFetchTime(Date.now())

        // Keep the selected report in sync with the reloaded page. Read from
        // the ref (not state) so fetchPage needs no selectedReportId dependency.
        const currentSelectedId: number | null = selectedReportIdRef.current
        if (currentSelectedId !== null) {
          const match: JournalArticle | undefined = data.items.find(
            (r: JournalArticle): boolean => r.sourceId === currentSelectedId
          )
          setSelectedReport(match ?? null)
        }
      } catch (err) {
        if (currentFetchId !== fetchIdRef.current) {
          return
        }
        setError(err instanceof Error ? err.message : "Failed to load reports")
      } finally {
        if (currentFetchId === fetchIdRef.current) {
          setLoading(false)
        }
      }
    },
    [getAccessToken, sidebarOption, searchQuery, pageSize, activeFilters, sortConfig]
  )

  /**
   * Set the page size, reset to the first page, and close the details panel.
   *
   * @param size - The new page size.
   */
  const setPageSize = useCallback((size: number): void => {
    setPageSizeState(size)
    setPageIndex(0)
    setSelectedReport(null)
    setSelectedReportId(null)
  }, [])

  /**
   * Set the sidebar option, reset to the first page, clear the search, and
   * close the details panel.
   *
   * @param option - The new sidebar option.
   */
  const setSidebarOption = useCallback((option: ReportsSidebarOption): void => {
    setSidebarOptionState(option)
    setPageIndex(0)
    setSelectedReport(null)
    setSelectedReportId(null)
    setSearchQueryState("")
  }, [])

  /**
   * Set the search query, reset to the first page, and close the details panel.
   *
   * @param query - The new search query.
   */
  const setSearchQuery = useCallback((query: string): void => {
    setSearchQueryState(query)
    setPageIndex(0)
    setSelectedReport(null)
    setSelectedReportId(null)
  }, [])

  /**
   * Set the active filters, reset to the first page, and close the details panel.
   *
   * @param filters - The new filter map.
   */
  const setActiveFilters = useCallback((filters: Map<string, Set<string>>): void => {
    setActiveFiltersState(filters)
    setPageIndex(0)
    setSelectedReport(null)
    setSelectedReportId(null)
  }, [])

  /**
   * Set the sort configuration and reset to the first page.
   *
   * @param config - The new sort config, or null to clear.
   */
  const setSortConfig = useCallback((config: GenericSortConfig<ReportSortableKey> | null): void => {
    setSortConfigState(config)
    setPageIndex(0)
  }, [])

  /**
   * Toggle sort for a column key and reset to the first page.
   * Cycles asc → desc → no sort for the same column; a new column starts at asc.
   *
   * @param key - The column key to sort by.
   */
  const toggleSort = useCallback((key: ReportSortableKey): void => {
    setSortConfigState((prev): GenericSortConfig<ReportSortableKey> | null => {
      if (prev?.key !== key) {
        return { key, direction: "asc" }
      }
      if (prev.direction === "asc") {
        return { key, direction: "desc" }
      }
      return null
    })
    setPageIndex(0)
  }, [])

  /**
   * Set the view mode.
   *
   * @param mode - The new view mode.
   */
  const setViewMode = useCallback((mode: ViewMode): void => {
    setViewModeState(mode)
  }, [])

  /**
   * Set the visible columns.
   *
   * @param columns - The new set of visible column keys.
   */
  const setVisibleColumns = useCallback((columns: Set<string>): void => {
    setVisibleColumnsState(columns)
  }, [])

  /**
   * Select a report to view in the details panel.
   *
   * @param report - The report to select.
   */
  const selectReport = useCallback((report: JournalArticle): void => {
    setSelectedReport(report)
    setSelectedReportId(report.sourceId)
  }, [])

  /**
   * Clear the selected report (closes the details panel).
   */
  const clearSelectedReport = useCallback((): void => {
    setSelectedReport(null)
    setSelectedReportId(null)
  }, [])

  /**
   * Replace a report in the cached list, and the selection if it is the same
   * report (the details panel calls this after note and file updates).
   *
   * @param updated - The updated report object.
   */
  const updateReportInList = useCallback((updated: JournalArticle): void => {
    setReports((prev: JournalArticle[]): JournalArticle[] =>
      prev.map((r: JournalArticle): JournalArticle => (r.sourceId === updated.sourceId ? updated : r))
    )
    if (selectedReportId === updated.sourceId) {
      setSelectedReport(updated)
    }
  }, [selectedReportId])

  const value: ReportsContextValue = {
    // State
    reports,
    totalCount,
    totalPages,
    pageIndex,
    pageSize,
    sidebarOption,
    searchQuery,
    activeFilters,
    sortConfig,
    viewMode,
    visibleColumns,
    loading,
    error,
    lastFetchTime,
    selectedReport,
    selectedReportId,
    // Actions
    fetchPage,
    setPageIndex,
    setPageSize,
    setSidebarOption,
    setSearchQuery,
    setActiveFilters,
    setSortConfig,
    toggleSort,
    setViewMode,
    setVisibleColumns,
    selectReport,
    clearSelectedReport,
    updateReportInList,
  }

  return <ReportsContext.Provider value={value}>{children}</ReportsContext.Provider>
}

/**
 * Hook to access the reports context.
 *
 * @returns The reports context value.
 * @throws Error if used outside of a ReportsProvider.
 */
export function useReports(): ReportsContextValue {
  const context: ReportsContextValue | null = useContext(ReportsContext)
  if (!context) {
    throw new Error("useReports must be used within a ReportsProvider")
  }
  return context
}
